import { memo, useRef, useMemo, useCallback, useState, useEffect, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Crud, CrudMode, notification, rejectionError, transformCrudFormErrors, getSelectsOptions } from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudProps, SelectsOptions, ValidationError } from '@ohpen/enso-ui';
import {
  ProcurationFeeItem,
  ProcurationFeeItemsResponse,
  ChangeRequestOfNormalizedDraftProcurationFee,
  NormalizedDraftProcurationFee,
  ProcurationFeeCrudChangeResponse,
  UsageBatchResponse,
  ProcurationFeeItemValidationErrors,
  procurationFeesCheckUsage,
  procurationFeesCreateChangeRequest,
} from '@pmp/api';
import { crudToActionMap, getApiErrorMessage, changeRequestButtonMessage, changeRequestNotificationMessage } from '@pmp/utils';
import { CrudFormProps } from '@pmp/types';
import { useFeatureToggle } from '@pmp/hooks';

import { getFeesFormSchema } from '../formConfig';
import { reorderedItemErrors } from '../utils';

export const ProcurationFeesCrudForm = memo(
  ({ mode, record, valueLists, refreshTable, onClose }: CrudFormProps<ProcurationFeeItemsResponse>): ReactElement | null => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(false);
    const [isUsed, setIsUsed] = useState<boolean | null>(null);
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);

    useEffect(() => {
      if (isUsed !== null) return;
      if (mode !== CrudMode.EDIT) {
        setIsUsed(false);
        setOpen(true);
        return;
      }
      const { id } = record || {};

      if (!id) {
        notification().error({ title: t('global.errors.unexpected') });
        onClose();
        return;
      }
      procurationFeesCheckUsage({ body: [id] })
        .then(({ data: { items } }: { data: UsageBatchResponse }) => {
          const isUsed = items?.some(item => item.isUsed === true) ?? false;
          setIsUsed(isUsed);
          setOpen(true);
        })
        .catch(error => {
          notification().error({ title: getApiErrorMessage(error) });
          onClose();
        });
    }, [mode, record, isUsed, t, onClose]);

    const onConfirm: (formData: ProcurationFeeItemsResponse) => Promise<ValidationError[] | void> = useCallback(
      data => {
        if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

        const trailFees = data.feeItems
          .filter(x => x.procurationFeeItemType === 'TRAIL')
          .map((item: ProcurationFeeItem) => ({
            value: item.value,
            unit: item.unit,
            basis: item.basis,
            startingMonth: item.startingMonth,
            intervalInMonths: item.intervalInMonths,
            endingType: item.endingType,
          }));

        const completionFee = data.feeItems.find(x => x.procurationFeeItemType === 'COMPLETION');

        const dataToSend: NormalizedDraftProcurationFee = {
          id: data.id,
          name: data.name,
          trailFees,
          completionFee: {
            value: completionFee ? completionFee.value : null,
            unit: completionFee ? completionFee.unit : null,
            basis: completionFee ? completionFee.basis : null,
          },
        };

        const body: ChangeRequestOfNormalizedDraftProcurationFee = {
          data: dataToSend,
          actionType: crudToActionMap[mode],
        };
        return procurationFeesCreateChangeRequest({ body }).then(
          ({ data: { isValid, errors } }: { data: ProcurationFeeCrudChangeResponse }) => {
            if (!isValid) {
              const form = ref.current?.form;
              if (form) {
                const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
                if (errorFields.length) form.setFields(errorFields);
              }

              const itemErrors = errors?.itemErrors
                ? Object.fromEntries(
                    Object.entries(errors.itemErrors as ProcurationFeeItemValidationErrors).map(([index, itemErrors]) => {
                      return [
                        index,
                        {
                          ...itemErrors,
                        },
                      ];
                    }),
                  )
                : {};

              const feeItems = reorderedItemErrors(itemErrors);
              return transformCrudFormErrors({ ...errors, feeItems });
            }
            if (mode === CrudMode.EDIT || !enableFourEyes) refreshTable();
            notification().success({
              title: changeRequestNotificationMessage(t, enableFourEyes),
            });
          },
        );
      },
      [mode, refreshTable, t, enableFourEyes],
    );

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    const selectsOptions: SelectsOptions = useMemo(() => (valueLists && getSelectsOptions(valueLists)) || {}, [valueLists]);

    const title = useMemo(() => {
      if (!mode) return '';
      const titles: { [key: string]: string } = {
        [CrudMode.VIEW]: t('global.labels.viewX', { x: t('lender.procurationFees.labels.procurationFee') }),
        [CrudMode.CREATE]: t('enso.labels.createX', { x: t('lender.procurationFees.labels.procurationFee') }),
        [CrudMode.EDIT]: t('enso.labels.editX', { x: t('lender.procurationFees.labels.procurationFee') }),
      };
      return titles[mode];
    }, [mode, t]);

    const formSchema = useMemo(() => getFeesFormSchema(selectsOptions, mode, !!isUsed), [mode, selectsOptions, isUsed]);

    const initialValues = useMemo<ProcurationFeeItemsResponse | null>(() => {
      if (!record) return null;
      return record;
    }, [record]);

    const crudProps: CrudProps<ProcurationFeeItemsResponse> = useMemo(() => {
      return {
        ref,
        open,
        title,
        labels: {
          confirm: mode === CrudMode.EDIT ? t('enso.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
        },
        initialValues,
        mode,
        readonly: mode === CrudMode.VIEW,
        width: 'm',
        onClose: handleOnClose,
        onConfirm,
      };
    }, [open, title, t, initialValues, mode, handleOnClose, onConfirm, enableFourEyes]);

    if (isUsed === null) return null;

    return <Crud {...crudProps} schema={formSchema} />;
  },
);
